import {
  getDealRoomByKey,
  getDealRoomDetails,
  getDealRoomExternalByKey,
  getDealRoomExternalDetails,
} from '@app/api/deal-room.api';
import background1Img from '@app/assets/images/background-1.jpg';
import background2Img from '@app/assets/images/background-2.jpg';
import background3Img from '@app/assets/images/background-3.jpg';
import background4Img from '@app/assets/images/background-4.jpg';
import background5Img from '@app/assets/images/background-5.jpg';
import background6Img from '@app/assets/images/background-6.jpg';
import background7Img from '@app/assets/images/background-7.jpg';
import FullScreenLoader from '@app/components/common/FullScreenLoader/FullScreenLoader';
import { PageTitle } from '@app/components/common/PageTitle/PageTitle';
import { DealRoomDefault } from '@app/components/deal-room/DealRoomLanding/DealRoomDefault';
import { DealRoomInviteModal } from '@app/components/deal-room/DealRoomLanding/DealRoomInviteModal/DealRoomInviteModal';
import { DealRoomLanding } from '@app/components/deal-room/DealRoomLanding/DealRoomLanding';
import { DealRoomStatus } from '@app/constants/enums/deal-room';
import { notificationController } from '@app/controllers/notificationController';
import { useAppSelector } from '@app/hooks/reduxHooks';
import { useDialog } from '@app/hooks/useDialog';
import { IDealRoom, IDealRoomShare } from '@app/interfaces/deal-room';
import { IDealRoomCategory } from '@app/interfaces/deal-room-category';
import { getLocalStorage, readToken, setLocalStorage } from '@app/services/localStorage.service';
import { getFilteredOfferSectionSections } from '@app/utils/dealRoom';
import { contentDirection, getTabId } from '@app/utils/utils';
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

const getHomeImg = () => {
  const randomPick = Math.floor(Math.random() * 7) + 1;
  let homeImg = background1Img;
  switch (randomPick) {
    case 1:
      homeImg = background1Img;
      break;
    case 2:
      homeImg = background2Img;
      break;
    case 3:
      homeImg = background3Img;
      break;
    case 4:
      homeImg = background4Img;
      break;
    case 5:
      homeImg = background5Img;
      break;
    case 6:
      homeImg = background6Img;
      break;
    case 7:
      homeImg = background7Img;
      break;
    default:
      homeImg = background1Img;
  }
  return homeImg;
};

const DealRoomLandingPage: React.FC = () => {
  const { id, key } = useParams<{ id: string; key: string }>();
  const [searchParams] = useSearchParams();
  const user = useAppSelector((state) => state.user.user);
  const translation = useAppSelector((state) => state.translation);
  const homeImg = getHomeImg();

  const [loader, setLoader] = useState<boolean>(true);
  const [dealRoomDetails, setDealRoomDetails] = useState<IDealRoomShare | null>(null);
  const [dealRoomAgent, setDealRoomAgent] = useState<IDealRoomShare | null>(null);
  const [dealRoomPeople, setDealRoomPeople] = useState<IDealRoomShare['people'] | null>(null);
  const [dealRoom, setDealRoom] = useState<IDealRoomShare | null>(null);
  const { dialogOpen, handleDialogOpen, handleDialogClose } = useDialog();
  const [showMessage, setShowMessage] = useState(false);
  const token = readToken();

  const updateDefaultHash = (dealRoom: IDealRoom) => {
    const defaultSection = dealRoom.sections?.find((section) => section.default);
    if (defaultSection) {
      window.location.hash = defaultSection.section || (defaultSection.category as IDealRoomCategory)?.id || '';
      return;
    }
    const parentDefaultSection = dealRoom.sections?.find((section) => section.subMenu?.some((sub) => sub.default));
    if (parentDefaultSection && parentDefaultSection.subMenu) {
      const defaultSubMenu = parentDefaultSection.subMenu.find((sub) => sub.default);
      if (defaultSubMenu) {
        window.location.hash = defaultSubMenu.section || '';
        return;
      }
    }
    const offerSectionIndex = getFilteredOfferSectionSections(dealRoom.offerSections || [])?.findIndex(
      (section) => section.default,
    );
    if (offerSectionIndex > -1) {
      window.location.hash = `external-${offerSectionIndex + 1}`;
      return;
    }
  };

  const onFinishDealRoomInvite = async (data: { firstName: string; lastName: string; phone: string }) => {
    if (id && key) {
      setLoader(true);
      try {
        const sessionId = searchParams.get('sessionId') || null;
        const shareKey = searchParams.get('s') || null;
        const uniqueId = getLocalStorage(`${id}-uniqueId`) || null;
        const res = user
          ? await getDealRoomByKey(id, key, {
              tabId: getTabId() || null,
            })
          : await getDealRoomExternalByKey(id, key, {
              sessionId,
              shareKey,
              uniqueId,
              tabId: getTabId() || null,
              firstName: data.firstName,
              lastName: data.lastName || null,
              phone: data.phone,
            });
        if (!user) {
          setLocalStorage(`${id}-uniqueId`, res.uniqueId);
          if (!shareKey) {
            const sParams = new URLSearchParams();
            sParams.set('s', res.shareKey);
            window.history.replaceState({}, '', `${window.location.pathname}?${sParams}`);
          }
        }
        updateDefaultHash(res);
        setDealRoom(res);
        if (res.dealRoomPeople) {
          setDealRoomPeople(res?.peoples?.find((p) => p.id === res.dealRoomPeople) || null);
        }
        handleDialogClose();
      } catch (error) {
        notificationController.error({ message: (error as Error).message });
      } finally {
        setLoader(false);
      }
    }
  };

  useEffect(() => {
    async function fetchDealRoomDetail(id: string, key: string) {
      setLoader(true);
      try {
        const sessionId = searchParams.get('sessionId') || null;
        const uniqueId = getLocalStorage(`${id}-uniqueId`) || null;

        const data = user
          ? await getDealRoomDetails(id, key)
          : await getDealRoomExternalDetails(id, key, {
              sessionId,
              uniqueId,
            });
        if (!data || (!user && data.status !== DealRoomStatus.OPEN_FOR_CUSTOMER)) {
          setShowMessage(true);
          setLoader(false);
          setDealRoomAgent(data);
          return;
        }
        setDealRoomDetails(data);
        setDealRoomPeople(data?.people || null);
        if ((user?.id && user) || data?.people) {
          const uData = data?.people
            ? {
                email: data.people.email,
                firstName: data.people.firstName || '',
                lastName: data.people.lastName || '',
                phone: data.people.phone || '',
              }
            : {
                email: user?.email || '',
                firstName: user?.firstname || '',
                lastName: user?.lastname || '',
                phone: user?.phone || '',
              };

          onFinishDealRoomInvite(uData);
        } else {
          handleDialogOpen();
          setLoader(false);
        }
      } catch (error) {
        setLoader(false);
      }
    }

    if (id && key && ((token && user) || !token)) {
      fetchDealRoomDetail(id, key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, key, searchParams, user]);

  return (
    <div dir={contentDirection(translation.isRtl)}>
      {loader && <FullScreenLoader />}
      {showMessage && dealRoomAgent && <DealRoomDefault dealRoomAgent={dealRoomAgent} />}
      {dealRoomDetails && (
        <>
          <PageTitle>{dealRoomDetails?.title || ''}</PageTitle>
          <DealRoomInviteModal
            dealRoom={dealRoomDetails}
            isOpen={dialogOpen}
            loading={loader}
            user={user}
            onFinish={onFinishDealRoomInvite}
          />
        </>
      )}
      {dealRoom && (
        <>
          <PageTitle>{dealRoom?.title || ''}</PageTitle>
          <DealRoomLanding dealRoom={dealRoom} homeImg={homeImg} people={dealRoomPeople || null} />
        </>
      )}
    </div>
  );
};

export default DealRoomLandingPage;
